/**
 * The actions that can be taken to trigger a change in the redux store state.
 * Each one must return an object that describes the action or, if thunk is being used as middleware,
 * a function that takes a 'dispatch' argument and an optional 'getState' argument.
 * These are used in #redux.connect
 * 'dispatch' is called to trigger a specific 'action' and provide some optional data for that action.
 * These actions are handled by the 'reducers' to alter the store state
 */
import {ACTION_TYPE} from './types'

/**
 * Fetch actions config
 * @typedef {Object} FETCH_ACTIONS
 * @property {string} REQUEST The action to dispatch just before the request is made
 * @property {string} RESPONSE The action to dispatch when a successful response is returned
 * @property {string} FAIL The action to dispatch if the request fails
 */

/**
 * Generic POST functionality to create a REQUEST - RESPONSE dispatch path while retrieving data
 * @param {string} endpoint The endpoint to POST the request to
 * @param {FETCH_ACTIONS} actions The actions to dispatch as the POST is performed
 * @param {Object} body The body to send with the POST request
 * @param {string}[idToken = ''] The authorization token if required by the endpoint
 * @returns {function(dispatch)} A redux action function
 */
const post = (endpoint, actions, body, idToken = '') => (
	(dispatch) => {
    dispatch({ type: actions.REQUEST, method: 'POST', request: body })
    return fetch(endpoint, {
      method: 'POST',
			body: JSON.stringify(body),
			headers: new Headers({
				'Content-Type': 'application/json',
				'Authorization': idToken
			})
		})
    .then(response => response.json()
    .then(json => dispatch({
      type: (response.ok ? actions.RESPONSE : actions.FAIL),
      body: json,
      request: body
    })))
    .catch(err => dispatch({
      type: actions.FAIL,
      body: { errorMessage: err.message },
      request: body
    }))
	}
)

/**
 * Generic DELETE functionality to create a REQUEST - RESPONSE dispatch path while retrieving data
 * @param {string} endpoint The endpoint to POST the request to
 * @param {FETCH_ACTIONS} actions The actions to dispatch as the POST is performed
 * @param {Object} body The body to send with the POST request
 * @param {string}[idToken = ''] The authorization token if required by the endpoint
 * @returns {function(dispatch)} A redux action function
 */
const del = (endpoint, actions, body, idToken = '') => (
	(dispatch) => {
    dispatch({ type: actions.REQUEST, method: 'DELETE', request: body })
		return fetch(endpoint, {
			method: 'DELETE',
			body: JSON.stringify(body),
			headers: new Headers({
				'Content-Type': 'application/json',
				'Authorization': idToken
			})
		})
		.then(response => response.json()
    .then(json => dispatch({
      type: (response.ok ? actions.RESPONSE : actions.FAIL),
      body: json,
      request: body
    })))
    .catch(err => dispatch({
      type: actions.FAIL,
      body: { errorMessage: err.message },
      request: body
    }))
	}
)

/**
 * Generic GET functionality to create a REQUEST - RESPONSE dispatch path while retrieving data
 * @param {string} endpoint The endpoint to POST the request to
 * @param {FETCH_ACTIONS} actions The actions to dispatch as the POST is performed
 * @param {string}[idToken = ''] The authorization token if required by the endpoint
 * @returns {function(dispatch)} A redux action function
 */
const get = (endpoint, actions, idToken = '') => (
	(dispatch) => {
    dispatch({ type: actions.REQUEST, method: 'GET' })
		fetch(endpoint, {
			method: 'GET',
			headers: new Headers({
				'Content-Type': 'application/json',
				'Authorization': idToken
			})
		})
			.then(response => response.json()
				.then(json => dispatch({type: (response.ok ? actions.RESPONSE : actions.FAIL), body: json})))
            .catch(err => dispatch({type: actions.FAIL, body: {errorMessage: err.message}}))
	}
)

export const login = (endpoint) => (
	(username, password) => (
		post(endpoint,
			{
				REQUEST: ACTION_TYPE.LOGIN_REQUEST,
				RESPONSE: ACTION_TYPE.LOGIN_RESPONSE,
				FAIL: ACTION_TYPE.LOGIN_RESPONSE_FAIL
			},
			{
				Username: username,
				Password: password
			})
	)
)

export const refresh = (endpoint, refreshToken) => (
	() => (
		post(endpoint,
			{
				REQUEST: ACTION_TYPE.REFRESH_REQUEST,
				RESPONSE: ACTION_TYPE.REFRESH_RESPONSE,
				FAIL: ACTION_TYPE.REFRESH_RESPONSE_FAIL
			},
			{
				RefreshToken: refreshToken
			})
	)
)

export const signUp = (endpoint) => (
	(username, password, email) => (
		post(endpoint,
			{
				REQUEST: ACTION_TYPE.SIGNUP_REQUEST,
				RESPONSE: ACTION_TYPE.SIGNUP_RESPONSE,
				FAIL: ACTION_TYPE.SIGNUP_RESPONSE_FAIL
			},
			{
				Username: username,
				Password: password,
				Email: email
			})
	)
)

export const confirmSignUp = (endpoint) => (
	(username, confirmationCode) => (
		post(endpoint,
			{
				REQUEST: ACTION_TYPE.CONFIRM_SIGNUP_REQUEST,
				RESPONSE: ACTION_TYPE.CONFIRM_SIGNUP_RESPONSE,
				FAIL: ACTION_TYPE.CONFIRM_SIGNUP_RESPONSE_FAIL
			},
			{
				Username: username,
				ConfirmationCode: confirmationCode
			})
	)
)

export const resendConfirmationCode = (endpoint) => (
	(username) => (
		post(endpoint,
			{
				REQUEST: ACTION_TYPE.RESEND_CONFIRMATION_REQUEST,
				RESPONSE: ACTION_TYPE.RESEND_CONFIRMATION_RESPONSE,
				FAIL: ACTION_TYPE.RESEND_CONFIRMATION_RESPONSE_FAIL
			},
			{
				Username: username
			}
		)
	)
)

export const addDomain = (endpoint, accessToken, idToken) => (
	(domain) => (
		post(endpoint,
			{
				REQUEST: ACTION_TYPE.ADD_DOMAIN_REQUEST,
				RESPONSE: ACTION_TYPE.ADD_DOMAIN_RESPONSE,
				FAIL: ACTION_TYPE.ADD_DOMAIN_RESPONSE_FAIL
			},
			{
				Domain: domain,
				AccessToken: accessToken
			},
			idToken)
	)
)

export const addPaymentMethod = (endpoint, accessToken, idToken) => (
    (paymentMethod) => (
        post(endpoint,
            {
                REQUEST: ACTION_TYPE.ADD_PAYMENT_METHOD_REQUEST,
                RESPONSE: ACTION_TYPE.ADD_PAYMENT_METHOD_RESPONSE,
                FAIL: ACTION_TYPE.ADD_PAYMENT_METHOD_RESPONSE_FAIL
            },
            {
                PaymentMethod: paymentMethod,
                AccessToken: accessToken
            },
            idToken)
    )
)

export const addDomainResource = (endpoint, accessToken, idToken) => (
	(resourceUrl) => (
		post(endpoint,
			{
				REQUEST: ACTION_TYPE.ADD_DOMAIN_RESOURCE_REQUEST,
				RESPONSE: ACTION_TYPE.ADD_DOMAIN_RESOURCE_RESPONSE,
				FAIL: ACTION_TYPE.ADD_DOMAIN_RESOURCE_RESPONSE_FAIL
			},
			{
				URL: resourceUrl,
				AccessToken: accessToken
			},
			idToken)
	)
)

export const delDomainResource = (endpoint, accessToken, idToken) => (
	(resourceUrl) => (
		del(endpoint,
			{
				REQUEST: ACTION_TYPE.DEL_DOMAIN_RESOURCE_REQUEST,
				RESPONSE: ACTION_TYPE.DEL_DOMAIN_RESOURCE_RESPONSE,
				FAIL: ACTION_TYPE.DEL_DOMAIN_RESOURCE_RESPONSE_FAIL
			},
			{
				URL: resourceUrl,
				AccessToken: accessToken
			},
			idToken)
	)
)

export const getResources = (endpoint, idToken) => (
	() => (
		get(endpoint,
			{
				REQUEST: ACTION_TYPE.GET_RESOURCES_REQUEST,
				RESPONSE: ACTION_TYPE.GET_RESOURCES_RESPONSE,
				FAIL: ACTION_TYPE.GET_RESOURCES_RESPONSE_FAIL
			},
			idToken)
	)
)


// WEBPACK FOOTER //
// ./src/redux/endpointActions.js